import React, { useMemo } from 'react';
import { FiCalendar, FiClock, FiSlash } from 'react-icons/fi';

const DAYS = [
  { key: 'monday', label: 'Lun' },
  { key: 'tuesday', label: 'Mar' },
  { key: 'wednesday', label: 'Mié' },
  { key: 'thursday', label: 'Jue' },
  { key: 'friday', label: 'Vie' },
  { key: 'saturday', label: 'Sáb' },
  { key: 'sunday', label: 'Dom' },
];

// "09:00" -> "9:00 a.m." / "13:30" -> "1:30 p.m."
const to12h = (hhmm) => {
  if (!hhmm) return '';
  const [h, m] = hhmm.split(':').map(Number);
  if (isNaN(h)) return hhmm;
  const ampm = h >= 12 ? 'p.m.' : 'a.m.';
  let h12 = h % 12;
  if (h12 === 0) h12 = 12;
  return `${h12}:${String(m).padStart(2, '0')} ${ampm}`;
};

// "09:00-18:00" -> "9:00 a.m. – 6:00 p.m."
const formatRange12h = (range) => {
  if (!range) return '';
  const [start, end] = range.split('-');
  return `${to12h(start)} – ${to12h(end)}`;
};

// Fecha "2026-06-05" -> "5 jun"
const formatShortDate = (yyyyMmDd) => {
  if (!yyyyMmDd) return '';
  const d = new Date(yyyyMmDd + 'T00:00:00');
  if (isNaN(d.getTime())) return yyyyMmDd;
  return d.toLocaleDateString('es-PE', { day: 'numeric', month: 'short' });
};

const InstructorAvailabilityCard = ({ instructor }) => {
  const availability = useMemo(() => instructor?.availability || {}, [instructor]);
  const blocked = useMemo(() => instructor?.diasBloqueados || [], [instructor]);

  const availableDays = useMemo(
    () => DAYS.filter((d) => Array.isArray(availability[d.key]) && availability[d.key].length > 0),
    [availability]
  );

  // Si todos los días disponibles comparten el mismo único rango, mostrarlo unificado.
  const unifiedRange = useMemo(() => {
    if (availableDays.length === 0) return null;
    const first = availability[availableDays[0].key];
    if (first.length !== 1) return null;
    const ref = first[0];
    const allSame = availableDays.every((d) => {
      const r = availability[d.key];
      return r.length === 1 && r[0] === ref;
    });
    return allSame ? ref : null;
  }, [availableDays, availability]);

  if (!instructor) return null;

  return (
    <div className="mt-2 px-3 py-2 bg-indigo-50/40 border-l-2 border-indigo-300 rounded-r-lg text-[10px] space-y-1.5">
      <div className="flex items-center gap-1.5 flex-wrap">
        <FiCalendar size={11} className="text-indigo-500 shrink-0" />
        <span className="font-semibold text-gray-600">Días:</span>
        {availableDays.length === 0 ? (
          <span className="text-gray-400 italic">Sin disponibilidad configurada</span>
        ) : (
          availableDays.map((d) => (
            <span
              key={d.key}
              className="px-1.5 py-0.5 bg-emerald-100 text-emerald-700 rounded font-medium"
            >
              {d.label}
            </span>
          ))
        )}
      </div>

      {availableDays.length > 0 && (
        <div className="flex items-start gap-1.5 flex-wrap">
          <FiClock size={11} className="text-indigo-500 shrink-0 mt-0.5" />
          <span className="font-semibold text-gray-600">Horario:</span>
          {unifiedRange ? (
            <span className="text-gray-700">{formatRange12h(unifiedRange)}</span>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {availableDays.map((d) => (
                <span key={d.key} className="text-gray-700">
                  <span className="font-medium">{d.label}:</span>{' '}
                  {availability[d.key].map(formatRange12h).join(', ')}
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      {blocked.length > 0 && (
        <div className="flex items-center gap-1.5 flex-wrap">
          <FiSlash size={11} className="text-red-500 shrink-0" />
          <span className="font-semibold text-gray-600">Bloqueado:</span>
          {blocked.map((b, i) => (
            <span
              key={`${b.fecha}-${i}`}
              className="px-1.5 py-0.5 bg-red-100 text-red-700 rounded font-medium"
              title={b.motivo || ''}
            >
              {formatShortDate(b.fecha)}
              {b.motivo ? ` · ${b.motivo}` : ''}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

export default InstructorAvailabilityCard;
